/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Call, CallDisconnectStatus, CallStatus } from '../typings';
import { CallHeader } from './CallHeader';
import UserIdentityCard from '../../customers/molecules/UserIdentityCard';
import CallMeta from './CallMeta';
import { CallAction } from './CallActions';

const ExpandedView = ({
  call,
  isMuted,
  isSpeakerOff,
  toggleMute,
  toggleSpeaker,
  handleChatClick,
  onRequestAccept,
  onCloseChip,
  onMinimize,
  handleDisconnect,
}: {
  call: Call & { callStartTime?: number };
  isMuted: boolean;
  isSpeakerOff: boolean;
  toggleMute: () => void;
  toggleSpeaker: () => void;
  handleChatClick: () => void;
  onRequestAccept: (call: Call) => void;
  onCloseChip: (call: Call) => void;
  onMinimize?: (call: Call) => void;
  handleDisconnect: (status: CallDisconnectStatus, reason?: string) => Promise<void>;
}) => {
  return (
    <div
      role="dialog"
      css={css`
        position: fixed;
        right: 16px;
        top: 8px;
        width: 304px;
        background: #fff;
        border-radius: 16px;
        border: 1px solid var(--divider, rgba(30, 41, 59, 0.14));
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px 0 rgba(0, 0, 0, 0.05);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        z-index: 9999;
        ${call.status === CallStatus.INITIATED && 'animation: slideIn 0.3s ease-out;'}
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0%);
            opacity: 1;
          }
        }
      `}
      onClick={(e) => e.stopPropagation()}
    >
      <CallHeader
        state={call.status}
        wabaLabel={call.wabaName}
        wabaNumber={call.wabaNumber}
        onClose={() => onCloseChip(call)}
        onMinimize={() => onMinimize?.(call)}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px 16px 16px;
        `}
      >
        <UserIdentityCard
          dir={[CallStatus.IN_PROGRESS, CallStatus.Connecting, CallStatus.END].includes(call.status) ? 'column' : 'row'}
          name={call.customerName || 'Unknown'}
          phoneNumber={call.customerNumber}
          size="large"
        />
      </div>
      {[CallStatus.IN_PROGRESS, CallStatus.END].includes(call.status) && typeof call.callStartTime === 'number' && (
        <CallMeta callStatus={call.status} startTime={call.callStartTime} />
      )}
      <CallAction
        callState={call.status}
        onAnswer={() => onRequestAccept(call)}
        onReject={() => handleDisconnect(CallStatus.END, 'Call declined')}
        onMuteToggle={toggleMute}
        onSpeakerToggle={toggleSpeaker}
        onChatClick={handleChatClick}
        onClose={() => onCloseChip(call)}
        isMuted={isMuted}
        isSpeakerOn={!isSpeakerOff}
      />
    </div>
  );
};

export default ExpandedView;